// src/features/auth/signup/Signup.jsx
import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { FiX, FiHome, FiKey, FiBriefcase } from 'react-icons/fi';
import 'react-toastify/dist/ReactToastify.css';
import SocialButtons from './SocialButtons';
import FormSection from './FormSection';
import { registerUser, googleAuth } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const ROLES = [
    {
        value: 'arrendatario',
        icon: FiHome,
        label: 'Arrendatario',
        desc: 'Busco propiedad para arrendar',
        color: 'from-brand-500 to-brand-600',
        ring: 'ring-brand-500',
    },
    {
        value: 'arrendador',
        icon: FiKey,
        label: 'Arrendador',
        desc: 'Tengo propiedades para arrendar',
        color: 'from-emerald-500 to-emerald-600',
        ring: 'ring-emerald-500',
    },
    {
        value: 'inmobiliaria',
        icon: FiBriefcase,
        label: 'Inmobiliaria',
        desc: 'Gestiono propiedades como empresa',
        color: 'from-purple-500 to-purple-600',
        ring: 'ring-purple-500',
    },
];

const Signup = ({ closeModal, setIsLoginView }) => {
    const { register, setValue, handleSubmit, formState: { errors } } = useForm();
    const { loginWithBackend } = useAuth();
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const navigate = useNavigate();
    const isGoogleAuthConfigured = Boolean(GOOGLE_CLIENT_ID);

    // Step 1: Role selection → Step 2: Form
    const showForm = selectedRole !== null;

    const onSubmit = async (data) => {
        try {
            toast.info('Registrando usuario...', { autoClose: 2000 });

            const fullName = `${data.nombre} ${data.apellido}`.trim();
            const payload = { ...data, nombre: fullName, rol: selectedRole };
            delete payload.apellido;

            const result = await registerUser(payload);

            if (result.needs_completion) {
                loginWithBackend(result.user, result.token);
                toast.info('Completa tus datos para finalizar el registro', { autoClose: 3000 });
                if (closeModal) closeModal();
                navigate(`/complete-registration/${result.persona_id}`);
                return;
            }

            if (result.user) {
                loginWithBackend(result.user, result.token);
            }

            toast.success('Usuario registrado correctamente', { autoClose: 3000 });
            if (closeModal) closeModal();
            navigate('/dashboard');
        } catch (error) {
            toast.error(`Error: ${error.message}`, { autoClose: 5000 });
        }
    };

    const handleGoogleResponse = useCallback(async (response) => {
        try {
            const result = await googleAuth(response.credential, selectedRole || 'arrendatario');
            loginWithBackend(result.user, result.token);

            if (result.needs_completion) {
                toast.info('Completa tus datos para finalizar', { autoClose: 3000 });
                if (closeModal) closeModal();
                navigate(`/complete-registration/${result.persona_id}`);
                return;
            }

            toast.success(`¡Bienvenido, ${result.user.nombre || 'usuario'}!`, { autoClose: 3000 });
            if (closeModal) closeModal();
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.message || 'Error al registrarse con Google', { autoClose: 5000 });
        } finally {
            setIsPopupOpen(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRole]);

    const signInWithGoogle = () => {
        if (isPopupOpen) return;

        if (!isGoogleAuthConfigured) {
            toast.error('Google Sign-In no configurado. Falta REACT_APP_GOOGLE_CLIENT_ID.', { autoClose: 5000 });
            return;
        }

        setIsPopupOpen(true);

        if (window.google?.accounts?.id) {
            window.google.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                callback: handleGoogleResponse,
            });
            window.google.accounts.id.prompt((notification) => {
                if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                    const btn = document.createElement('div');
                    btn.id = '_g_signup_tmp';
                    btn.style.display = 'none';
                    document.body.appendChild(btn);
                    window.google.accounts.id.renderButton(btn, { type: 'icon' });
                    btn.querySelector('div[role=button]')?.click();
                    setTimeout(() => btn.remove(), 100);
                    setIsPopupOpen(false);
                }
            });
        } else {
            toast.error('Google Sign-In no disponible. Recarga la página.', { autoClose: 5000 });
            setIsPopupOpen(false);
        }
    };

    return (
        <>
            <ToastContainer />
            <div className="w-full max-w-2xl mx-auto">
                <div className="card overflow-hidden">
                    {/* Header */}
                    <div className="p-6 text-center bg-gradient-to-b from-brand-900 to-brand-800 dark:from-brand-900 dark:to-surface-850 relative">
                        <h5 className="text-white text-xl font-bold">
                            {showForm ? 'Crear Cuenta' : 'Elige tu perfil'}
                        </h5>
                        <p className="text-brand-200 text-sm mt-1">
                            {showForm
                                ? `Registro como ${ROLES.find(r => r.value === selectedRole)?.label}`
                                : 'Selecciona el tipo de cuenta que necesitas'}
                        </p>
                        {closeModal && (
                            <button
                                className="absolute top-4 right-4 p-1 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
                                onClick={closeModal}
                            >
                                <FiX className="w-5 h-5" />
                            </button>
                        )}
                    </div>

                    <div className="p-6 space-y-5">
                        {!showForm ? (
                            /* Step 1: Role selector */
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    {ROLES.map(({ value, icon: Icon, label, desc, color, ring }) => (
                                        <button
                                            key={value}
                                            onClick={() => setSelectedRole(value)}
                                            className={`card p-5 text-center group cursor-pointer hover:scale-[1.03] transition-all duration-300
                                                        ${selectedRole === value ? `ring-2 ${ring}` : ''}`}
                                        >
                                            <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${color} shadow-lg mb-3 group-hover:shadow-xl transition-shadow`}>
                                                <Icon className="w-6 h-6 text-white" />
                                            </div>
                                            <h3 className="font-semibold text-surface-900 dark:text-white text-sm">{label}</h3>
                                            <p className="text-xs text-surface-500 dark:text-surface-400 mt-1">{desc}</p>
                                        </button>
                                    ))}
                                </div>

                                <p className="text-center text-sm text-surface-500 dark:text-surface-400">
                                    ¿Ya tienes una cuenta?{' '}
                                    <span
                                        className="text-brand-500 hover:text-brand-400 font-medium cursor-pointer transition-colors"
                                        onClick={() => setIsLoginView(true)}
                                    >
                                        Inicia sesión
                                    </span>
                                </p>
                            </>
                        ) : (
                            /* Step 2: Registration form */
                            <>
                                <button
                                    onClick={() => setSelectedRole(null)}
                                    className="text-sm text-brand-500 hover:text-brand-400 font-medium transition-colors flex items-center gap-1"
                                >
                                    ← Cambiar perfil
                                </button>

                                <SocialButtons
                                    signInWithGoogle={signInWithGoogle}
                                    isPopupOpen={isPopupOpen}
                                    isGoogleAuthConfigured={isGoogleAuthConfigured}
                                />

                                <div className="relative text-center">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-surface-200 dark:border-surface-700"></div>
                                    </div>
                                    <span className="relative bg-white dark:bg-surface-850 px-3 text-sm text-surface-400 dark:text-surface-500">
                                        o completa tu información
                                    </span>
                                </div>

                                <FormSection
                                    register={register}
                                    setValue={setValue}
                                    handleSubmit={handleSubmit}
                                    onSubmit={onSubmit}
                                    errors={errors}
                                    selectedRole={selectedRole}
                                />

                                <p className="text-center text-sm text-surface-500 dark:text-surface-400">
                                    ¿Ya tienes una cuenta?{' '}
                                    <span
                                        className="text-brand-500 hover:text-brand-400 font-medium cursor-pointer transition-colors"
                                        onClick={() => setIsLoginView(true)}
                                    >
                                        Inicia sesión
                                    </span>
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Signup;
